/**
 * Canvas Context (V2)
 *
 * Provides APIs for programmatic canvas manipulation.
 * Components inside the canvas tree use useCanvas() to add/remove/update
 * nodes and edges without directly importing React Flow hooks.
 *
 * Key V2 changes:
 * - addEdge enforces areTypesCompatible() (BUILD_ORDER Q1)
 * - Uses crypto.randomUUID() via createNode (BUILD_ORDER Q2)
 * - Separate from NodeHoverContext (separate file)
 */

import { createContext, useContext, useCallback, useMemo } from 'react'
import { useReactFlow, type Node, type Edge } from 'reactflow'
import { areTypesCompatible } from './portTypes'
import { nodeSchemas, createNode } from './nodeRegistry'

/**
 * Position on the canvas
 */
export interface CanvasPosition {
  x: number
  y: number
}

/**
 * Canvas API interface - all 10 methods per spec
 */
export interface CanvasContextValue {
  // DAG manipulation
  addNode: (
    type: string,
    position: CanvasPosition,
    data?: Record<string, unknown>,
    options?: { autoExecute?: boolean }
  ) => string
  addEdge: (
    source: string,
    sourceHandle: string,
    target: string,
    targetHandle: string
  ) => void
  removeNode: (nodeId: string) => void
  removeEdge: (edgeId: string) => void
  duplicateNode: (nodeId: string) => string | undefined

  // Node data
  updateNodeData: (nodeId: string, updates: Record<string, unknown>) => void
  persistNodeData: (nodeId: string, updates: Record<string, unknown>) => void
  updateNodeParent: (nodeId: string, parentId: string | null) => void
  updateNodePosition: (nodeId: string, x: number, y: number) => void

  // Execution
  executeNode: (nodeId: string) => void

  // State access
  getNode: (nodeId: string) => Node | undefined
  getNodes: () => Node[]
}

/**
 * Canvas context - null outside of a provider
 */
export const CanvasContext = createContext<CanvasContextValue | null>(null)

/**
 * Hook to access canvas APIs.
 * @throws Error if used outside of CanvasProvider
 */
export function useCanvas(): CanvasContextValue {
  const ctx = useContext(CanvasContext)
  if (!ctx) {
    throw new Error('useCanvas must be used within a CanvasProvider')
  }
  return ctx
}

/**
 * Optional hook that returns null instead of throwing when outside provider.
 */
export function useCanvasOptional(): CanvasContextValue | null {
  return useContext(CanvasContext)
}

// =============================================================================
// Hook to build the CanvasContextValue from React Flow internals
// =============================================================================

/**
 * Resolves the port type for a given node and handle.
 * Returns undefined if the schema or port is not found.
 */
function resolvePortType(
  nodeType: string | undefined,
  handleId: string,
  direction: 'inputs' | 'outputs'
): string | undefined {
  if (!nodeType) return undefined
  const schema = nodeSchemas[nodeType]
  if (!schema) return undefined
  const ports = schema[direction]
  const port = ports.find((p) => p.id === handleId)
  return port?.type
}

/**
 * Hook that builds a CanvasContextValue from React Flow state.
 * Used by the workspace page to provide context to all canvas nodes.
 *
 * @param onUpdateNodeData - callback for user-initiated config changes (Y doc + debounced backend sync)
 * @param onPersistNodeData - callback for execution results (Y doc only, no backend sync)
 * @param onExecuteNode - callback to queue a node for execution
 */
export function useCanvasContextValue(
  onUpdateNodeData?: (nodeId: string, updates: Record<string, unknown>) => void,
  onPersistNodeData?: (nodeId: string, updates: Record<string, unknown>) => void,
  onExecuteNode?: (nodeId: string) => void
): CanvasContextValue {
  const { getNode, getNodes, setNodes, setEdges } = useReactFlow()

  const addNode = useCallback(
    (
      type: string,
      position: CanvasPosition,
      data?: Record<string, unknown>,
      options?: { autoExecute?: boolean }
    ): string => {
      const node = createNode(type, position, data)
      setNodes((nds) => [...nds, node])
      if (options?.autoExecute && onExecuteNode) {
        onExecuteNode(node.id)
      }
      return node.id
    },
    [setNodes, onExecuteNode]
  )

  const addEdge = useCallback(
    (source: string, sourceHandle: string, target: string, targetHandle: string): void => {
      // Validate type compatibility (BUILD_ORDER Q1)
      const sourceNode = getNode(source)
      const targetNode = getNode(target)

      const sourcePortType = resolvePortType(sourceNode?.type, sourceHandle, 'outputs')
      const targetPortType = resolvePortType(targetNode?.type, targetHandle, 'inputs')

      // If both port types are found, enforce compatibility
      // If either is unknown (dynamic ports, unknown node type), allow the connection
      if (sourcePortType && targetPortType) {
        if (!areTypesCompatible(sourcePortType, targetPortType)) {
          return // Silent no-op on invalid connections
        }
      }

      const edgeId = `e-${source}-${sourceHandle}-${target}-${targetHandle}`
      const newEdge: Edge = {
        id: edgeId,
        source,
        sourceHandle,
        target,
        targetHandle,
      }
      setEdges((eds) => [...eds, newEdge])
    },
    [getNode, setEdges]
  )

  const removeNode = useCallback(
    (nodeId: string): void => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
    },
    [setNodes, setEdges]
  )

  const removeEdge = useCallback(
    (edgeId: string): void => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId))
    },
    [setEdges]
  )

  const duplicateNode = useCallback(
    (nodeId: string): string | undefined => {
      const node = getNode(nodeId)
      if (!node) return undefined

      const clone = structuredClone(node)
      clone.id = crypto.randomUUID()
      clone.position = {
        x: node.position.x + 50,
        y: node.position.y + 50,
      }
      clone.selected = false
      if (clone.data) {
        clone.data.status = 'idle'
        delete clone.data.error
      }
      setNodes((nds) => [...nds, clone])
      return clone.id
    },
    [getNode, setNodes]
  )

  const updateNodeData = useCallback(
    (nodeId: string, updates: Record<string, unknown>): void => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
        )
      )
      onUpdateNodeData?.(nodeId, updates)
    },
    [setNodes, onUpdateNodeData]
  )

  const persistNodeData = useCallback(
    (nodeId: string, updates: Record<string, unknown>): void => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...updates } } : n
        )
      )
      onPersistNodeData?.(nodeId, updates)
    },
    [setNodes, onPersistNodeData]
  )

  const executeNode = useCallback(
    (nodeId: string): void => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, status: 'running' } } : n
        )
      )
      onExecuteNode?.(nodeId)
    },
    [setNodes, onExecuteNode]
  )

  return useMemo(
    () => ({
      addNode,
      addEdge,
      removeNode,
      removeEdge,
      duplicateNode,
      updateNodeData,
      persistNodeData,
      updateNodeParent: () => {},
      updateNodePosition: () => {},
      executeNode,
      getNode,
      getNodes,
    }),
    [
      addNode,
      addEdge,
      removeNode,
      removeEdge,
      duplicateNode,
      updateNodeData,
      persistNodeData,
      executeNode,
      getNode,
      getNodes,
    ]
  )
}
